import {
    FLIGHT_LEG_PHASE_FINISH,
    FLIGHT_LEG_PHASE_START,
    type FlightLegPhase,
    isLegFinished as sharedLegIsFinished,
} from '@eflight/shared/dist/common/flight/legPhase';

import { type InputSources } from '../../utils/InputSources';
import { type CountableChange } from '../CountableChanges/types';
import { type DbSaveable } from '../LocalDB/types';
import { type InLegLogbook } from '../Logbook/InLegLogbook';
import { type HandSignature } from '../util/SignatureTypes';
import { type CrewRoleTypes } from './CrewRoleTypes';
import { type FlightOps } from './FlightOpsTypes';

// (#3057) Definice bydlí ve sdíleném balíčku — pravidlo „leg je dokončený" potřebuje i palivový
// engine, který běží v lambdě a odsud importovat nesmí. Tady zůstává jen re-export, aby se
// aplikační importy nemusely přepisovat.
export { FLIGHT_LEG_PHASE_FINISH, FLIGHT_LEG_PHASE_START, type FlightLegPhase };

export interface StartedFlightLeg extends DbSaveable {
    id: string;
    createdAt: Date;
    flightId: string;
    aircraftId: string;
    airportOffIcao: string;
    ops: FlightOps | undefined;
    securityCheckDate: Date | null;
    securityCheckName: string | null;
    preflightCheckDate: Date | null;
    preflightCheckName: string | null;
    picName: string;
    picId: string | null;
    picRoles: CrewRoleTypes[];
    sicName: string | null;
    sicId: string | null;
    sicRoles: CrewRoleTypes[];
    deicing: Deicing | null;
    notes: string;
    startSignature: HandSignature | null;
    phase: FlightLegPhase;
    startedAt: Date;
    upliftCountableChangeId: string;
    editableByUserIds: string[];
    inputSources: InputSources;
    timeOut: Date; // so that secondary index on DynamoDB table works
    reservationId?: string;
    reservationEventId?: string;
    ticketId?: string | null;
}

export type WithLastCountableChange = {
    lastCountableChange: CountableChange;
};

export interface FinishedFlightLeg extends StartedFlightLeg {
    phase: typeof FLIGHT_LEG_PHASE_FINISH;
    airportOnIcao: string;
    timeOff: Date;
    timeOn: Date;
    timeIn: Date;
    logbookFields: InLegLogbook | null;
    finishNotes: string;
    finishSignature: HandSignature | null;
    consumptionCountableChangeId: string;
}

export type FlightLeg = FinishedFlightLeg | StartedFlightLeg;
/**
 * (#3057) Test SAMOTNÝ dělá sdílená `isLegFinished`; tady zůstává jen typový guard, který
 * sdílená verze poskytnout nemůže (nezná aplikační `FinishedFlightLeg`). Pravidlo je tím na
 * jednom místě pro aplikaci, tabulku Fuel optimizeru i engine.
 */
export const isLegFinished = (leg: FlightLeg): leg is FinishedFlightLeg => sharedLegIsFinished(leg);

/**
 * Pořadí legů v rámci letu: dokončené podle block-off, rozpracovaný vždy až za nimi. Jeho `timeOut` i `createdAt`
 * jsou jen zástupná půlnoc dne, takže by se jinak zařadil před dnešní dokončené legy.
 */
export const compareLegsInFlight = (a: FlightLeg, b: FlightLeg): number => {
    const aFinished = isLegFinished(a);
    if (aFinished !== isLegFinished(b)) {
        return aFinished ? -1 : 1;
    }

    return aFinished ? a.timeOut.getTime() - b.timeOut.getTime() : a.createdAt.getTime() - b.createdAt.getTime();
};

export type Deicing = {
    startTime: Date | null;
    fluidType: string;
    ratio: string;
    holdOverTime: Date | null;
};

// The rule lives in @eflight/shared next to the duty calculator, which also uses it in the lambda.
export { isSupervisingInstructorOnLeg } from '@eflight/shared/dist/duties/FinishedFlightLegLike';
